import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Loader, Loader2, Trash2, X } from 'lucide-react';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import toast from 'react-hot-toast';
import { AppDataTable } from '../shared/table/AppDataTable.tsx';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { createConfigureProcessesColumns } from './processes/processTableColumns.tsx';
import { useProcessListPopover } from './processes/useProcessListPopover.ts';
import { ProcessListPopover } from './processes/ProcessListPopover.tsx';
import {
  clearProcessDraftCache,
  isLocalDraftProcessId,
  processDraftQueryKey,
  processUuidForApi
} from './processes/functional';
import {
  buildProcessReturnState,
  currentPathWithSearch,
  soxProcessCreatePath,
} from '../routing/processNavigation.ts';
import { TENANT_PATHS } from '../routing/paths.ts';
import { useProcessBackNavigation } from '../hooks/useProcessBackNavigation.ts';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
  normalizePageSize,
} from '../shared/table/pagination.ts';

function ProcessesScreen() {
  const { cycleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { listPopover, setListPopover } = useProcessListPopover();
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [processPage, setProcessPage] = useState(1);
  const [processPageSize, setProcessPageSize] = useState(DEFAULT_PAGE_SIZE);
  const { selectedAuditYear } = useAuditYear();
  const isLocked = Boolean(selectedAuditYear?.is_locked);

  const { data, isLoading, isFetching, isPlaceholderData, error } = useQuery({
    queryKey: ['cycle-processes', cycleId, processPage, processPageSize],
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = {
        page: processPage,
        page_size: processPageSize,
      };
      const response = await axiosInstance.get(API_ENDPOINTS.getProcesses(cycleId), { params });
      const root = response?.data ?? {};
      const processes = Array.isArray(root.processes) ? root.processes : [];
      const pagination = root.pagination ?? {};
      return {
        processes,
        totalItems: Number.isFinite(Number(pagination.total_items)) ? Number(pagination.total_items) : processes.length,
        totalPages: Number.isFinite(Number(pagination.total_pages)) ? Math.max(1, Number(pagination.total_pages)) : 1,
      };
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (processId) => {
      if (isLocalDraftProcessId(processId)) {
        return { mode: 'draft', processId };
      }
      await axiosInstance.delete(API_ENDPOINTS.deleteProcess(processId));
      return { mode: 'server', processId };
    },
    onSuccess: async (result) => {
      if (result?.mode === 'draft') {
        const prev = queryClient.getQueryData(processDraftQueryKey(cycleId));
        const next = (prev?.processes ?? []).filter((p) => p.id !== result.processId);
        if (next.length === 0) {
          clearProcessDraftCache(queryClient, cycleId);
        } else {
          queryClient.setQueryData(processDraftQueryKey(cycleId), { ...prev, processes: next });
        }
        toast.success('Draft process removed');
        await queryClient.invalidateQueries({ queryKey: ['cycle-processes', cycleId] });
        return;
      }
      toast.success('Process deleted');
      await queryClient.invalidateQueries({ queryKey: ['cycle-processes', cycleId] });
      await queryClient.invalidateQueries({ queryKey: ['sox-cycles'] });
    },
    onError: (err) => toast.error(resolve403ErrorMessage(err, 'Failed to delete process'))
  });

  const rows = useMemo(() => (Array.isArray(data?.processes) ? data.processes : []), [data]);
  const totalItems = data?.totalItems ?? 0;
  const totalPages = data?.totalPages ?? 1;

  useEffect(() => {
    setProcessPage((currentPage) => Math.min(Math.max(1, currentPage), Math.max(1, totalPages)));
  }, [totalPages]);

  const tablePagination = useMemo(
    () => ({
      pageIndex: Math.max(0, processPage - 1),
      pageSize: processPageSize,
    }),
    [processPage, processPageSize]
  );

  const handleTablePaginationChange = useCallback(
    (updater) => {
      const current = {
        pageIndex: Math.max(0, processPage - 1),
        pageSize: processPageSize,
      };
      const next = typeof updater === 'function' ? updater(current) : updater;

      if (next.pageSize !== processPageSize) {
        setProcessPageSize(normalizePageSize(next.pageSize, DEFAULT_PAGE_SIZE_OPTIONS));
        setProcessPage(1);
        return;
      }

      setProcessPage(Math.min(Math.max(1, next.pageIndex + 1), Math.max(1, totalPages)));
    },
    [processPage, processPageSize, totalPages]
  );

  const processReturnPath = useMemo(() => currentPathWithSearch(location), [location]);
  const { goBack: navigateBackFromProcessesList } = useProcessBackNavigation(TENANT_PATHS.SOX_DASHBOARD);

  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Fetch every page of processes for this cycle so we can delete them all.
  // The current table view only holds the visible page; without this loop a
  // "delete all" would only nuke whatever fit on screen and leave the rest.
  const fetchAllProcesses = useCallback(async () => {
    const PAGE_SIZE = 100;
    const all = [];
    let page = 1;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const response = await axiosInstance.get(API_ENDPOINTS.getProcesses(cycleId), {
        params: { page, page_size: PAGE_SIZE },
      });
      const batch = Array.isArray(response.data?.processes) ? response.data.processes : [];
      all.push(...batch);
      const pagesTotal = Number(response.data?.pagination?.total_pages) || 1;
      if (page >= pagesTotal || batch.length === 0) break;
      page += 1;
    }
    return all;
  }, [cycleId]);

  const handleDeleteAllProcesses = useCallback(async () => {
    if (!cycleId) return;
    let all;
    try {
      all = await fetchAllProcesses();
    } catch (err) {
      console.error('Failed to fetch processes for bulk delete:', err);
      toast.error('Failed to load processes for delete-all');
      return;
    }
    const deletable = all.filter((p) => !isLocalDraftProcessId(p?.id) && p?.id);
    if (deletable.length === 0) {
      toast('No processes to delete', { icon: 'ℹ️' });
      return;
    }
    if (!window.confirm(
      `Permanently delete ALL ${deletable.length} process(es) in cycle ${String(cycleId).toUpperCase()}?\n\n`
      + `This cannot be undone.`
    )) return;
    if (!window.confirm(`Final check — deleting ${deletable.length} process(es). Proceed?`)) return;

    setBulkDeleting(true);
    let failures = 0;
    let lastError = null;
    for (const p of deletable) {
      try {
        await axiosInstance.delete(API_ENDPOINTS.deleteProcess(p.id));
      } catch (err) {
        console.error(`Failed to delete process ${p.name || p.id}:`, err);
        lastError = err;
        failures += 1;
      }
    }
    setBulkDeleting(false);
    if (failures > 0) {
      toast.error(
        resolve403ErrorMessage(
          lastError,
          `Deleted ${deletable.length - failures} of ${deletable.length}. ${failures} failed.`
        )
      );
    } else {
      toast.success(`Deleted ${deletable.length} process(es)`);
    }
    await queryClient.invalidateQueries({ queryKey: ['cycle-processes', cycleId] });
    await queryClient.invalidateQueries({ queryKey: ['sox-cycles'] });
  }, [cycleId, fetchAllProcesses, queryClient]);

  const columnOptions = useMemo(
    () => ({
      processReturnPath,
      onDeleteRequest: (row) =>
        setDeleteConfirm({
          id: processUuidForApi(row),
          name: String(row.name || '').trim() || 'this process'
        })
    }),
    [processReturnPath]
  );

  const columns = useMemo(
    () => createConfigureProcessesColumns(cycleId, navigate, deleteMutation, setListPopover, columnOptions),
    [cycleId, columnOptions, deleteMutation, navigate, setListPopover]
  );

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-gray-900" />
      </div>
    );
  }

  if (error) {
    return <div className="p-8 text-sm text-red-700">Failed to load processes.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={navigateBackFromProcessesList}
              className="rounded-full p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            >
              <ChevronLeft size={18} />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Configure Processes</h1>
              <p className="text-sm text-gray-500">Cycle: {String(cycleId || '').toUpperCase()}</p>
            </div>
          </div>
          <button
            onClick={navigateBackFromProcessesList}
            className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-7xl p-8">
        <div className="mb-4">
          <ReadOnlyBanner />
        </div>
        <div className="mb-4 flex items-center justify-between">
          <div />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDeleteAllProcesses}
              disabled={isLocked || bulkDeleting || isLoading || totalItems === 0}
              className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
              title={isLocked ? 'Audit year is locked' : 'Permanently delete every process in this cycle'}
            >
              {bulkDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete all
            </button>
            <button
              type="button"
              disabled={isLocked}
              onClick={() =>
                navigate(soxProcessCreatePath(String(cycleId || '')), {
                  state: buildProcessReturnState(processReturnPath),
                })
              }
              className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
              title={isLocked ? 'Audit year is locked' : undefined}
            >
              Create Process
            </button>
          </div>
        </div>

        <AppDataTable
          data={rows}
          columns={columns}
          mode="server"
          loading={isLoading || (isFetching && !isPlaceholderData && rows.length === 0)}
          emptyMessage="No processes found. Create a process to get started."
          paginated
          paginationItemLabel="processes"
          showPageSizeSelector
          pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
          pagination={tablePagination}
          onPaginationChange={handleTablePaginationChange}
          rowCount={totalItems}
          pageCount={totalPages}
          getRowId={(row, index) => String(row.id ?? row.process_id ?? `row-${index}`)}
        />
        <ProcessListPopover listPopover={listPopover} />

        {deleteConfirm ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl">
              <h2 className="text-lg font-semibold text-gray-900">Delete process</h2>
              <p className="mt-2 text-sm text-gray-600">
                Are you sure you want to delete{' '}
                <span className="font-medium text-gray-900">&quot;{deleteConfirm.name}&quot;</span>? This cannot be undone.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleteMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                  disabled={deleteMutation.isPending}
                  onClick={() => {
                    deleteMutation.mutate(deleteConfirm.id, {
                      onSettled: () => setDeleteConfirm(null)
                    });
                  }}
                >
                  {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ProcessesScreen;
